/**
 * ScopeViolationBadge — sidebar notification for 403 insufficient_scope events.
 *
 * Polls /api/scope-violations/recent every 30s. When `total > 0`, renders a
 * pulsing red badge with the count. Clicking opens a dropdown panel listing
 * grouped violations (by key + required_scope) with two CTAs per group:
 *   - "Upgrade permissions" → /settings (jumps to the key in question)
 *   - "Dismiss"             → POST /scope-violations/acknowledge {key_name}
 *
 * Renders inline in the dashboard sidebar between dev-portal section and
 * the bottom items.
 */
import React, { useEffect, useState, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, X, ShieldAlert } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 30_000;

export default function ScopeViolationBadge({ collapsed, onOpenSettings }) {
  const [data, setData] = useState({ total: 0, groups: [] });
  const [open, setOpen] = useState(false);
  const pollRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/scope-violations/recent`, { withCredentials: true });
      setData(r.data || { total: 0, groups: [] });
    } catch (_e) {
      // Unauthenticated visitors / pre-login state — silently skip
    }
  }, []);

  useEffect(() => {
    refresh();
    pollRef.current = setInterval(refresh, POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [refresh]);

  const dismissKey = async (keyName) => {
    try {
      await axios.post(`${API}/scope-violations/acknowledge`,
        { key_name: keyName }, { withCredentials: true });
      toast.success(`Dismissed violations for ${keyName}`);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to acknowledge");
    }
  };

  const dismissAll = async () => {
    try {
      await axios.post(`${API}/scope-violations/acknowledge`,
        { all: true }, { withCredentials: true });
      toast.success("All scope alerts cleared");
      setOpen(false);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to acknowledge");
    }
  };

  const handleUpgrade = (keyName) => {
    setOpen(false);
    if (onOpenSettings) onOpenSettings(keyName);
  };

  const groups = data.groups || [];
  const hasAlerts = data.total > 0;

  if (collapsed) {
    return (
      <button
        onClick={() => setOpen(true)}
        data-testid="scope-violation-badge-collapsed"
        title={hasAlerts ? `${data.total} scope-denied request${data.total === 1 ? "" : "s"}` : "Scope alerts (none)"}
        className="relative w-full flex justify-center py-2.5 rounded-lg text-[var(--qn-foreground-muted)] hover:text-[var(--qn-foreground)] hover:bg-[var(--qn-surface-hover)]"
      >
        <Bell className="w-4 h-4" />
        {hasAlerts && (
          <span className="absolute top-1 right-1 min-w-[16px] h-[16px] px-1 text-[9px] font-bold leading-none flex items-center justify-center rounded-full bg-red-500 text-white animate-pulse">
            {data.total > 99 ? "99+" : data.total}
          </span>
        )}
      </button>
    );
  }

  return (
    <div className="relative" data-testid="scope-violation-badge">
      <button
        onClick={() => setOpen(o => !o)}
        data-testid="scope-violation-badge-toggle"
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
          hasAlerts
            ? "bg-red-500/8 text-red-300 hover:bg-red-500/12 border-l-2 border-red-500/60"
            : "text-[var(--qn-foreground-muted)] hover:text-[var(--qn-foreground)] hover:bg-[var(--qn-surface-hover)]"
        }`}
      >
        <Bell className={`w-4 h-4 shrink-0 ${hasAlerts ? "animate-pulse" : ""}`} />
        <span className="flex-1 text-left">Scope alerts</span>
        {hasAlerts && (
          <span data-testid="scope-violation-count" className="min-w-[20px] px-1.5 h-5 text-[10px] font-bold flex items-center justify-center rounded-full bg-red-500 text-white">
            {data.total > 99 ? "99+" : data.total}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.15 }}
            data-testid="scope-violation-panel"
            className="absolute left-full top-0 ml-2 w-[380px] bg-[var(--qn-card)] border border-[var(--qn-border)] rounded-lg shadow-2xl z-50"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--qn-border)]">
              <div className="flex items-center gap-2">
                <ShieldAlert className="w-4 h-4 text-red-400" />
                <span className="text-sm font-medium">Scope-denied requests</span>
                <span className="text-[10px] text-[var(--qn-foreground-muted)] font-mono">last 60 min</span>
              </div>
              <button onClick={() => setOpen(false)} className="text-[var(--qn-foreground-muted)] hover:text-[var(--qn-foreground)]">
                <X className="w-4 h-4" />
              </button>
            </div>

            {groups.length === 0 ? (
              <div className="px-4 py-6 text-center text-xs text-[var(--qn-foreground-muted)]">
                No scope-denied requests in the last hour. Your portfolio companies are operating within their granted permissions.
              </div>
            ) : (
              <>
                <div className="max-h-[380px] overflow-y-auto">
                  {groups.map((g, i) => (
                    <div
                      key={`${g.key_name}-${g.required_scope}-${i}`}
                      data-testid={`violation-group-${i}`}
                      className="px-4 py-3 border-b border-[var(--qn-border)] last:border-b-0 hover:bg-[var(--qn-surface-hover)]/50"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-[var(--qn-foreground)] truncate">
                            {g.key_name}
                          </div>
                          <div className="text-[11px] text-[var(--qn-foreground-muted)] mt-0.5">
                            {g.project} · {new Date(g.last_seen).toLocaleTimeString([], {hour:"2-digit",minute:"2-digit"})}
                          </div>
                          <div className="mt-1.5 text-[11px] leading-relaxed text-[var(--qn-foreground)]">
                            Tried <span className="text-red-300 font-mono">{g.required_scope}</span>
                            <span className="text-[var(--qn-foreground-muted)]"> but only has </span>
                            <span className="text-teal-300 font-mono">{(g.granted_scopes || []).join(", ") || "no scopes"}</span>
                          </div>
                        </div>
                        <span className="shrink-0 text-[10px] bg-red-500/15 text-red-300 px-1.5 py-0.5 rounded font-mono">
                          x{g.count}
                        </span>
                      </div>
                      <div className="mt-2 flex gap-2">
                        <button
                          onClick={() => handleUpgrade(g.key_name)}
                          data-testid={`upgrade-${g.key_name}`}
                          className="px-2.5 py-1 text-[10px] uppercase tracking-[0.15em] text-[var(--qn-primary)] border border-[var(--qn-primary)]/40 rounded hover:bg-[var(--qn-primary)]/10"
                        >
                          Upgrade permissions
                        </button>
                        <button
                          onClick={() => dismissKey(g.key_name)}
                          data-testid={`dismiss-${g.key_name}`}
                          className="px-2.5 py-1 text-[10px] uppercase tracking-[0.15em] text-[var(--qn-foreground-muted)] border border-[var(--qn-border)] rounded hover:text-[var(--qn-foreground)]"
                        >
                          Dismiss
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="px-4 py-2 border-t border-[var(--qn-border)] flex justify-between items-center">
                  <span className="text-[10px] text-[var(--qn-foreground-muted)]">
                    {data.total} total event{data.total === 1 ? "" : "s"}
                  </span>
                  <button
                    onClick={dismissAll}
                    data-testid="dismiss-all-violations"
                    className="text-[10px] uppercase tracking-[0.15em] text-[var(--qn-foreground-muted)] hover:text-[var(--qn-foreground)]"
                  >
                    Dismiss all
                  </button>
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
